import { IBaseComponentProps, NSID } from "namirasoft-site-react";
import { NSSection, NSPanel, NSButtonBlue, NSLine, NSSpace, NSSpaceSizeType } from 'namirasoft-site-react';
import { NSARouterMakerProps } from "../NSARouterMakerProps";
import { Component, createRef, ReactNode } from "react";
import { IEntityInfo } from "../IEntityInfo";
import { IStorageLocal, NamingConvention } from "namirasoft-core";
import { NSASectionViewTabPage, NSASectionViewTabPageUIProps } from "./NSASectionViewTabPage";
import { NSASectionEditTabPage, NSASectionEditTabPageEditorProps, NSASectionEditTabPageUIProps } from "./NSASectionEditTabPage";
import { CategoryRow, FieldRow } from "namirasoft-field";

export interface NSASectionEditCustomProps<RowType extends { id: string }, RowTypeInput = RowType> extends NSARouterMakerProps, IBaseComponentProps
{
	isEdit: boolean;
	ui: {
		panel?: IBaseComponentProps;
		tab_page_edit: {
			ui?: NSASectionEditTabPageUIProps;
			editor: NSASectionEditTabPageEditorProps<RowType, RowTypeInput>;
		};
		tab_page_view?: NSASectionViewTabPageUIProps;
	}
	onRowLoaded?: (item: RowType) => void;
}

export interface NSASectionEditProps<RowType extends { id: string }, RowTypeInput = RowType> extends NSASectionEditCustomProps<RowType, RowTypeInput>, NSARouterMakerProps
{
	model: {
		id: string | null;
		row: RowType | null;
	};
	entity: IEntityInfo<RowType, RowTypeInput>,
	ctf: {
		enabled: boolean;
		fields: FieldRow[] | null;
		categories: CategoryRow[] | null;
	};
	deleteFieldCache: () => void;
	children: ReactNode;
}

interface NSASectionEditState<RowType>
{
	row: RowType | null;
}

export class NSASectionEdit<RowType extends { id: string }, RowTypeInput = RowType> extends Component<NSASectionEditProps<RowType, RowTypeInput>, NSASectionEditState<RowType>>
{
	NSASectionViewTabPage_Ref = createRef<NSASectionViewTabPage<RowType, RowTypeInput>>();
	NSASectionEditTabPage_Ref = createRef<NSASectionEditTabPage<RowType, RowTypeInput>>();

	constructor(props: NSASectionEditProps<RowType, RowTypeInput>)
	{
		super(props);

		this.state = { row: this.props.model.row };

		this.reload = this.reload.bind(this);
		this.getRow = this.getRow.bind(this);
		this.onApply = this.onApply.bind(this);
	}
	reload() 
	{
		this.setState(prev => ({ ...prev, row: null }));
		if (this.props.model.id)
			if (this.props.entity.server.get)
				this.props.entity.server.get(this.props.model.id).then(row =>
				{
					this.setState(prev => ({ ...prev, row }), () =>
					{
						this.props.onRowLoaded?.(row);
					});
				}).catch(() => { });
	}
	getRow()
	{
		return this.state.row;
	}
	onApply(onFinshied: () => void): void
	{
		try
		{
			let Name_Pascal_Space = NamingConvention.lower_case_underscore.convert(this.props.entity.name, NamingConvention.Pascal_Case_Space);

			let onSuccess = (new_row: RowType, message: string) =>
			{
				this.props.notifier.onSuccess(message);
				if (this.props.entity.client.onSuccess)
					this.props.entity.client.onSuccess(new_row);
				else
				{
					let url = null;
					if (this.props.entity.client.getViewURL)
						url = this.props.entity.client.getViewURL(new_row.id);
					else if (this.props.entity.client.getListURL)
						url = this.props.entity.client.getListURL();
					if (url)
						this.props.url.redirect(url, {});
				}
			};
			let cur_row: RowTypeInput | null = this.NSASectionEditTabPage_Ref.current?.getInputRow() as RowTypeInput | null;
			if (cur_row)
				if (this.props.isEdit)
				{
					if (this.props.model.id)
					{
						if (this.props.entity.client.onUpdate)
						{
							this.props.entity.client.onUpdate(this.props.model.id, cur_row).then(new_entity =>
							{
								if (new_entity)
									onSuccess(new_entity, Name_Pascal_Space + " has been updated successfully");
								if (this.props.model.id)
									NSID.clearID(new IStorageLocal(), this.props.model.id);
								onFinshied();
							}).catch(onFinshied);
						}
						else if (this.props.entity.server.update)
						{
							this.props.entity.server.update(this.props.model.id, cur_row).then(new_entity =>
							{
								onSuccess(new_entity, Name_Pascal_Space + " has been updated successfully");
								if (this.props.model.id)
									NSID.clearID(new IStorageLocal(), this.props.model.id);
								onFinshied();
							}).catch(onFinshied);
						}
						else
							onFinshied();
					}
					else
						throw new Error("ID was not provided.");
				}
				else
				{
					if (this.props.entity.client.onCreate)
					{
						this.props.entity.client.onCreate(cur_row).then(new_entity =>
						{
							if (new_entity)
								onSuccess(new_entity, Name_Pascal_Space + " has been created successfully");
							onFinshied();
						}).catch(onFinshied);
					}
					else if (this.props.entity.server.create)
					{
						this.props.entity.server.create(cur_row).then(new_entity =>
						{
							onSuccess(new_entity, Name_Pascal_Space + " has been created successfully");;
							onFinshied();
						}).catch(onFinshied);
					}
					else
						onFinshied();
				}
			else
				onFinshied();
		} catch (error: any)
		{
			this.props.notifier.onError(error);
			onFinshied();
		}
	}
	override componentDidMount()
	{
		if (this.props.model.id)
			if (!this.props.model.row || this.props.model.row.id !== this.props.model.id)
				this.reload();
	}
	override render()
	{
		return (
			<>
				<NSSection
					id={this.props.id}
					classList={[`${this.props.classList?.join(" ") ?? ""}`]}
					style={{ ...this.props.style }}
				>
					<NSPanel
						id={this.props.ui.panel?.id}
						classList={[`${this.props.ui.panel?.classList?.join(" ") ?? ""}`]}
						style={{ ...this.props.ui.panel?.style }}
					>
						<NSASectionEditTabPage<RowType, RowTypeInput>
							ref={this.NSASectionEditTabPage_Ref}
							{...this.props}
							editor={this.props.ui.tab_page_edit.editor}
							ui={this.props.ui.tab_page_edit.ui}
						>
							{this.props.children}
						</NSASectionEditTabPage>
					</NSPanel>
					<NSSpace size={NSSpaceSizeType.SMALL}></NSSpace>
					<NSButtonBlue
						title="Apply"
						onClick={{
							action: (onFinished) =>
							{
								this.onApply(onFinished);
							}
						}}
					/>
				</NSSection>
				{
					this.props.isEdit && (this.props.ui.tab_page_view?.enabled ?? true) &&
					<>
						<NSSpace size={NSSpaceSizeType.SMALL} />
						<NSLine />
						<NSSpace size={NSSpaceSizeType.SMALL} />
						<NSASectionViewTabPage<RowType, RowTypeInput>
							ref={this.NSASectionViewTabPage_Ref}
							{...this.props}
							ui={this.props.ui.tab_page_view}
						/>
					</>
				}
			</>
		);
	}
}